import React, {useRef} from 'react';
import PropTypes from 'prop-types';
import {graphql} from 'gatsby';
import get from 'lodash/get';

import Article from 'ssg-themes/src/themes/hellofinance/modules/Article/Article';
import ArticleHero from "ssg-themes/src/themes/hellofinance/modules/ArticleHero/ArticleHero";
import GenericHeading from 'ssg-themes/src/themes/hellofinance/modules/GenericHeading/GenericHeading';
import Container from 'ssg-themes/src/themes/hellofinance/components/Container/Container';
import Authorship from "ssg-themes/src/themes/hellofinance/modules/Authorship/Authorship";
import Cta from "ssg-themes/src/themes/hellofinance/modules/Cta/Cta";
import SimilarArticles from "ssg-themes/src/themes/hellofinance/modules/SimilarArticles/SimilarArticles";
import TableOfContents from "ssg-themes/src/themes/hellofinance/modules/TableOfContents/TableOfContents";
import Mediabar from "ssg-themes/src/themes/hellofinance/modules/Mediabar/Mediabar";
import RecentPosts from 'ssg-themes/src/themes/hellofinance/modules/RecentPosts/RecentPosts';
import mg from "ssg-themes/src/common/constants/schema/organization/mg";
import {organizationType} from "ssg-themes/src/common/structuredData/organizationType";

import {useSiteMetadata} from "../../hooks/useSiteMetadata";

import {parseArticleLong} from '../../utils/parseArticles';
import getBreadcrumbs from "../../utils/getBreadcrumbs";
import getReadingTime from '../../utils/getReadingTime';
import getStructuredData from "../../utils/getStrucutredData";
import parseAuthor from "../../utils/parseAuthor";
import replaceAdsProductIDs from '../../utils/replaceAdsProductIDs';
import replaceProductIDs from "../../utils/replaceProductIDs";
import replaceProductInfoTableIds from "../../utils/replaceProductInfoTableIds";
import replaceProductComparisonIds from "../../utils/replaceProductComparisonIds";

import {COMMON_DATA} from "../../data/common.data";
import {authorFromTypeToTitle} from '../../data/authorFromTypeToTitle';
import {tooltipData} from "../../data/blogPost.data";

import ScoreApi from "../../components/ScoreApi/ScoreApi";
import Seo from '../../components/Seo/Seo'

import {ICONS} from '../../constants/icons';
import {TEMPLATE_TYPE} from "../../constants/templateType";

import Base from '../base';

import './BlogList.scss';

/**
 * Props Type declaration
 */
const propTypes = {
  pageHeader: PropTypes.object
};

/**
 * Default props declaration
 */
const defaultProps = {};

/**
 * Blog List Template
 */
const BlogList = props => {
  const siteMetadata = useSiteMetadata();

  const category = get(props, 'data.contentfulBlogList');
  const postsModel = get(props, 'data.allContentfulBlogPost.edges');
  const ogImage = `${category?.seo?.image?.url ? category.seo.image?.url : category.featuredImage?.url}`;
  const articleRef = useRef();

  const isPostTemplate = category?.templateType === TEMPLATE_TYPE.BLOG_POST;

  const siteUrl = siteMetadata.siteUrl
  const categorySlug = `/${category.slug}`
  const categoryUrl = `${siteUrl}${categorySlug}`
  const articleSlug = `${categorySlug}`
  const articleUrl = `${siteUrl}${articleSlug}`
  const brandName = siteMetadata.brandName;

  const {faqList, votes, content, cleanContent, hasNoindex} = props.pageContext;

  const timeRequired = content ? getReadingTime(content) : null;

  const parsedContent =
    content
      .replaceAll('<table>', '<div class="table"><div class="tableWrapper"><table>')
      .replaceAll('</table>', '</table></div></div>')
      .replace(/(<img[^>]+>(?:<\/img>)?)/g, "$1")
      .replace(/<img/g, '<img loading="lazy"')
      .replace(/(?:{{\s*ad:\s*([^\s}]+)\s*}}|<p>{{\s*ad:\s*([^\s}]+)\s*}}<\/p>)/g, replaceAdsProductIDs.bind(this, props.pageContext.ads))
      .replace(/{{\s*product:\s*([^\s]+)\s*}}/g, replaceProductIDs.bind(this, props.pageContext.products))
      .replace(/{{\s*productInfoTable:\s*([^\s]+)\s*}}/g, replaceProductInfoTableIds.bind(this, props.pageContext.productInfoTables))
      .replace(/{{\s*productComparison:\s*([^\s]+)\s*}}/g, replaceProductComparisonIds.bind(this, props.pageContext.productComparisons));

  const breadcrumbs = getBreadcrumbs(
    [{
      label: 'Home',
      href: '/',
      title: 'Home',
      schemaHref: `${siteUrl}`
    },
      {
        label: category.shortHeading ? category.shortHeading : category.heading,
        schemaHref: `${siteUrl}${categorySlug}`,
      }
    ]);

  const structuredData = getStructuredData(category, breadcrumbs, categoryUrl, articleUrl, siteUrl, brandName, cleanContent, votes, faqList, timeRequired)
  const recentPosts = postsModel.map(({node}) => parseArticleLong(node));
  const posts = postsModel.map(({node}) => parseArticleLong(node));

  const authorsObject = {
    author: category?.author ? parseAuthor(category?.author) : false,
    editedBy: category?.editedBy ? parseAuthor(category?.editedBy) : false,
    reviewedBy: category?.reviewedBy ? parseAuthor(category?.reviewedBy) : false,
    factChecker: category?.factChecker ? parseAuthor(category?.factChecker) : false
  }

  const authors = Object.keys(authorsObject).filter(key => authorsObject[key]).map(key => authorsObject[key]);

  return (
    <Base>
      <Seo
        lang="pl"
        title={category.seo?.title}
        date={category.updatedAt}
        hasNoindex={hasNoindex}
        description={category.seo?.description}
        ogImage={ogImage}
        ogType={isPostTemplate ? 'article' : null}
        meta={[
          {
            property: 'entryID',
            content: category.contentful_id
          },
          {
            property: 'article:published_time',
            content: category.metaPublishedTime
          },
          {
            property: 'article:modified_time',
            content: category.metaUpdatedTime
          },
        ]}
        {...(!isPostTemplate && {
          structuredData: {
            breadcrumbs: {
              itemListElement: breadcrumbs.map(item => ({
                name: item.label,
                item: item.schemaHref
              }))
            },
            webPage: {
              url: categoryUrl,
              name: category?.seo?.name || category.heading,
              lastReviewed: category.metaUpdatedTime,
              reviewedBy: JSON.parse(organizationType(mg))
            }
          },
        })}
        {...(isPostTemplate && structuredData)}
      />
      <div className="blogListWrapper">
        {isPostTemplate && <><ArticleHero
          breadcrumbs={breadcrumbs}
          breadcrumbsIcon={ICONS.home}
          heading={category?.heading}
          description={category?.shortContent}
          backgroundPicture={category?.featuredImage?.url}
          i18n={tooltipData}
          author={category?.author && parseAuthor(category?.author, {title: authorFromTypeToTitle.author})}
          editedBy={category?.editedBy && parseAuthor(category?.editedBy, {title: authorFromTypeToTitle.editedBy})}
          reviewedBy={category?.reviewedBy && parseAuthor(category?.reviewedBy, {title: authorFromTypeToTitle.reviewedBy})}
          factChecker={category?.factChecker && parseAuthor(category?.factChecker, {title: authorFromTypeToTitle.factCheckedBy})}
          updatedAt={{
            label: authorFromTypeToTitle.updatedBy,
            date: category.updatedAt,
            datetime: category.authorFromTypeToTitle
          }}
          editedOn={{
            label: `${authorFromTypeToTitle.editedBy}:`,
            date: category.editedOn,
            datetime: category.editedOnDatetime
          }}
          reviewedOn={{
            label: `${authorFromTypeToTitle.reviewedBy}:`,
            date: category.reviewedOn,
            datetime: category.reviewedOnDatetime
          }}
          factCheckedOn={{
            label: `${authorFromTypeToTitle.factCheckedBy}:`,
            date: category.factCheckedOn,
            datetime: category.factCheckedOnDatetime
          }}
          readingTime={timeRequired && `${timeRequired} min `}
        />
          <Mediabar {...COMMON_DATA.mediabar} />
        </>
        }
        {(parsedContent && isPostTemplate) && <>
          <Container classname='blogList'>
            <div className='blogList_body' ref={articleRef}>
              {isPostTemplate &&
                <TableOfContents heading="Spisi treści:" ref={articleRef} classname='blogList_toc' id='toc'/>}
              {parsedContent &&
                <Article html={parsedContent}/>}
            </div>

            {isPostTemplate && <div className='blogList_footer'>
              <ScoreApi {...votes}  />
            </div>}
          </Container>
          {authors && authors.length > 0 &&
            <Authorship heading={COMMON_DATA.authorship.heading} link={COMMON_DATA.authorship.link} items={authors}/>}
          <Cta
            heading='Oszczędzaj czas i pieniądze z&nbsp;Hello&nbsp;Finance'
            description='Poznaj sprawdzone sposoby na finanse osobiste'
            button={{label: 'Zobacz inne treści', href: '/'}}
          />
          <SimilarArticles heading="Podobne artykuły" items={posts}/>
        </>}
        {!isPostTemplate && <>
          <GenericHeading heading={category.heading} breadcrumbsIcon={ICONS.home}
                          description={category.shortContent} breadcrumbs={breadcrumbs}
                          variant='Category'/>
          {recentPosts.length > 0 && <RecentPosts articles={recentPosts} variant="LightGray"/>}
        </>}
      </div>
    </Base>
  );
};

BlogList.propTypes = propTypes;
BlogList.defaultProps = defaultProps;
export const pageQuery = graphql`
  fragment authorFields on ContentfulAuthor {
    role
    firstName
    lastName
    muckrack
    linkedin
    slug
    hasAuthorPage
    description {
      description
    }
    image {
      url
    }
    imageLarge {
      url
    }
    email
    additionalName
    honorificSuffix
    honorificSuffixDescription
    knowsAbout
    hasOccupation
  }

  query BlogListBySlug(
    $slug: String!
  ) {
    contentfulBlogList(slug: { eq: $slug }) {
      contentful_id
      slug
      shortHeading
      heading
      shortContent
      node_locale
      featuredImage {
        url
      }
      author  {
        ...authorFields
      }
      editedBy {
        ...authorFields
      }
      reviewedBy {
        ...authorFields
      }
      factChecker {
        ...authorFields
      }
      templateType
      content {
        content
      }
      seo {
        title
        description
      }
      createdAt(locale: "pl-PL", formatString: "DD MMMM, YYYY")
      createdAtDatetime: createdAt(locale: "pl-PL", formatString: "YYYY-MM-DD")
      updatedAt(locale: "pl-PL", formatString: "DD MMMM, YYYY")
      updatedAtDatetime: updatedAt(locale: "pl-PL", formatString: "YYYY-MM-DD")
      editedOn(locale: "pl-PL", formatString: "DD MMMM, YYYY")
      editedOnDatetime: editedOn(locale: "pl-PL", formatString: "YYYY-MM-DD")
      reviewedOn(locale: "pl-PL", formatString: "DD MMMM, YYYY")
      reviewedOnDatetime: reviewedOn(locale: "pl-PL", formatString: "YYYY-MM-DD")
      factCheckedOn(locale: "pl-PL", formatString: "DD MMMM, YYYY")
      factCheckedOnDatetime: factCheckedOn(locale: "pl-PL", formatString: "YYYY-MM-DD")
      rawDate: createdAt
      metaPublishedTime: createdAt(formatString: "YYYY-MM-DDTHH:mm:ssZ")
      metaUpdatedTime: updatedAt(formatString: "YYYY-MM-DDTHH:mm:ssZ")
    }
    allContentfulBlogPost(
      filter: {category: {slug: { eq: $slug, ne: null }, node_locale: {eq: "pl-PL"}}, slug:{nin: [null]}, options:{nin: "Hide in listing"}}
      sort: {createdAt: DESC}
    ) {
      edges {
        node {
          node_locale
          slug
          thumbnailImage {
            url
          }
          heading
          options
          shortHeading
          category {
            slug
          }
          shortContent
          createdAt(locale: "pl-PL", formatString: "DD MMM")
          updatedAt(locale: "pl-PL", formatString: "DD MMM")
          rawDate: createdAt
          seo {
            title
          }
        }
      }
    }
  }
`;
export default BlogList;
